import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownNarrowWide,
  Compass,
  Database,
  FileSpreadsheet,
  FileText,
  Loader2,
  Radar,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import {
  downloadReportCsvFromGraph,
  downloadReportPdfFromGraph,
  fetchIngestStatus,
  fetchSummaryFromGraph,
  runIngestCognos,
  runScanFromGraph,
  type ComplianceReport,
  type GovernanceFinding,
  type GovernanceImpactSummary,
  type IngestRun,
  type IngestStatus,
  type ScanMode,
  type Severity,
} from "@/lib/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import { ContentPicker } from "@/components/ContentPicker";

type SeverityFilter = "all" | Severity;
type SortKey = "severity" | "rule" | "category" | "asset";

const DEFAULT_TENANT = "_default";

export function GovernancePage() {
  const [tenantId, setTenantId] = useState(DEFAULT_TENANT);
  const [scope, setScope] = useState("*");
  const [mode, setMode] = useState<ScanMode>("scan");
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [ruleFilter, setRuleFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("severity");
  const [groupByEntity, setGroupByEntity] = useState(false);

  const queryClient = useQueryClient();

  // Ingest status drives the "Refresh inventory" panel. Polling at 2s
  // while a run is in-flight gives the operator real-time progress
  // without hammering the Cognos SDK; the rest of the time the cache
  // is read-only.
  const ingestStatus = useQuery<IngestStatus>({
    queryKey: ["atlas", "ingest", "status"],
    queryFn: fetchIngestStatus,
    refetchInterval: (q) =>
      q.state.data?.current ? 2000 : false,
  });

  const scan = useMutation<ComplianceReport, Error>({
    mutationFn: () => runScanFromGraph(mode, tenantId, scope),
  });

  const summary = useMutation<GovernanceImpactSummary, Error>({
    mutationFn: () => fetchSummaryFromGraph(tenantId, scope),
  });

  const ingest = useMutation<IngestRun, Error>({
    mutationFn: () => runIngestCognos(tenantId, null, null),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["atlas", "ingest", "status"] }),
  });

  useEffect(() => {
    if (scan.isSuccess) summary.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan.isSuccess, scan.data]);

  async function onDownloadPdf() {
    setPdfDownloading(true);
    setDownloadError(null);
    try {
      await downloadReportPdfFromGraph(tenantId, scope);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfDownloading(false);
    }
  }

  async function onDownloadCsv() {
    setCsvDownloading(true);
    setDownloadError(null);
    try {
      await downloadReportCsvFromGraph(tenantId, scope);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setCsvDownloading(false);
    }
  }

  const lastRun = ingestStatus.data?.current ?? ingestStatus.data?.last ?? null;
  const cognosAvailable = ingestStatus.data?.sources?.cognos ?? false;
  const inventoryEmpty =
    !ingestStatus.isLoading &&
    !ingestStatus.data?.last &&
    !ingestStatus.data?.current;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-atlas-ink">
          Governance scan
        </h1>
        <p className="mt-0.5 text-sm text-atlas-slate">
          Atlas scans the live entity inventory in the unified metadata graph.
          Refresh from Cognos before scanning when the source has changed.
        </p>
      </header>

      <InventoryPanel
        run={lastRun}
        loading={ingestStatus.isLoading}
        cognosAvailable={cognosAvailable}
        ingestPending={ingest.isPending}
        ingestError={ingest.error?.message ?? null}
        onRefresh={() => ingest.mutate()}
      />

      <section data-tour-id="governance-rules" className="mt-4 grid gap-3 rounded-xl2 border border-atlas-slate/20 bg-white p-4 shadow-card">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tenant">
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="w-full rounded-md border border-atlas-slate/30 bg-white px-2 py-1 text-sm font-mono"
              placeholder="_default"
            />
          </Field>
          <Field label="Scope">
            <div className="flex items-center gap-2">
              <input
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="w-full rounded-md border border-atlas-slate/30 bg-white px-2 py-1 text-sm"
                placeholder="* for everything, or a Cognos folder path"
              />
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="shrink-0 rounded-md border border-atlas-slate/30 bg-white px-2 py-1 text-2xs font-medium text-atlas-ink hover:border-atlas-primaryDark"
                title="Browse Cognos folders"
              >
                Browse…
              </button>
            </div>
          </Field>
        </div>
        <Field label="Mode">
          <div className="flex flex-wrap gap-2 pt-1">
            <ModeButton current={mode} value="scan" label="Full scan" onClick={setMode} />
            <ModeButton current={mode} value="compliance" label="Compliance (BLOCKER only)" onClick={setMode} />
            <ModeButton current={mode} value="anomalies" label="Anomalies" onClick={setMode} />
          </div>
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => scan.mutate()}
            disabled={scan.isPending || inventoryEmpty}
            className="inline-flex items-center gap-1 rounded-md bg-atlas-primary px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-atlas-primaryDark disabled:opacity-40"
            title={inventoryEmpty ? "Refresh inventory before scanning" : undefined}
          >
            {scan.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : (mode === "anomalies" ? <Compass size={12} /> : <ShieldCheck size={12} />)}
            Run {mode === "scan" ? "scan" : mode === "compliance" ? "compliance check" : "anomaly scan"}
          </button>
          <button
            onClick={onDownloadCsv}
            disabled={csvDownloading || inventoryEmpty}
            className="inline-flex items-center gap-1 rounded-md border border-atlas-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-atlas-ink shadow-sm hover:bg-atlas-mist disabled:opacity-40"
            title="Download findings as CSV — one row per finding"
          >
            {csvDownloading ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
            Download CSV
          </button>
          <button
            onClick={onDownloadPdf}
            disabled={pdfDownloading || inventoryEmpty}
            className="inline-flex items-center gap-1 rounded-md border border-atlas-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-atlas-ink shadow-sm hover:bg-atlas-mist disabled:opacity-40"
            title="Render the full scan as a stakeholder-ready PDF"
          >
            {pdfDownloading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Download PDF
          </button>
        </div>
      </section>

      {scan.error && (
        <p className="mt-4 rounded-md border border-atlas-danger/30 bg-atlas-danger/5 p-3 text-sm text-atlas-danger">
          Scan failed — {scan.error.message}
        </p>
      )}
      {downloadError && (
        <p className="mt-4 rounded-md border border-atlas-danger/30 bg-atlas-danger/5 p-3 text-sm text-atlas-danger">
          Download failed — {downloadError}
        </p>
      )}

      {summary.data && <ImpactCard impact={summary.data} />}

      {scan.data && (
        <ReportView
          report={scan.data}
          search={search}
          setSearch={setSearch}
          severityFilter={severityFilter}
          setSeverityFilter={setSeverityFilter}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          ruleFilter={ruleFilter}
          setRuleFilter={setRuleFilter}
          sort={sort}
          setSort={setSort}
          groupByEntity={groupByEntity}
          setGroupByEntity={setGroupByEntity}
        />
      )}

      <ContentPicker
        open={pickerOpen}
        title="Pick a Cognos folder for the governance scan"
        initialPath={scope.startsWith("/") ? scope : undefined}
        onCancel={() => setPickerOpen(false)}
        onConfirm={(picked) => {
          setScope(picked.path);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

function InventoryPanel({
  run,
  loading,
  cognosAvailable,
  ingestPending,
  ingestError,
  onRefresh,
}: {
  run: IngestRun | null;
  loading: boolean;
  cognosAvailable: boolean;
  ingestPending: boolean;
  ingestError: string | null;
  onRefresh: () => void;
}) {
  const running = run?.status === "RUNNING" || ingestPending;
  return (
    <section
      data-tour-id="governance-inventory"
      className="rounded-xl2 border border-atlas-slate/20 bg-white p-4 shadow-card"
      aria-label="Inventory status"
    >
      <header className="mb-2 flex items-center gap-2 text-sm font-semibold text-atlas-ink">
        <Database size={14} aria-hidden /> Inventory
      </header>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-xs text-atlas-ink/80">
          {loading && <span className="text-atlas-slate">Checking inventory…</span>}
          {!loading && !run && cognosAvailable && (
            <span className="text-atlas-slate">
              No inventory yet. Click <span className="font-semibold">Refresh from Cognos</span> to crawl the content store.
            </span>
          )}
          {!loading && !run && !cognosAvailable && (
            <span className="text-atlas-slate">
              No inventory yet. Configure <span className="font-mono text-2xs">datumcraft.cognos.sdk.*</span> to enable Cognos ingest.
            </span>
          )}
          {run && (
            <div className="space-y-1">
              <div>
                <span className="font-medium text-atlas-ink">{run.source}</span>{" "}
                <span className="font-mono text-2xs text-atlas-slate">{run.rootPath}</span>{" "}
                <RunStatusBadge status={run.status} />
              </div>
              <div className="font-mono text-2xs text-atlas-slate">
                assets={run.assetsIngested} · users={run.usersIngested} · containers={run.containersWalked}
                {run.skipped > 0 && <> · skipped={run.skipped}</>}
                {run.truncated && <> · <span className="text-atlas-warn">truncated</span></>}
              </div>
              {run.finishedAt && (
                <div className="text-2xs text-atlas-slate">
                  Last refresh {timeAgo(run.finishedAt)}
                </div>
              )}
              {run.errorMessage && (
                <div className="text-2xs text-atlas-danger">{run.errorMessage}</div>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={running || !cognosAvailable}
          className="inline-flex items-center gap-1 rounded-md border border-atlas-slate/30 bg-white px-3 py-1.5 text-xs font-medium text-atlas-ink shadow-sm hover:bg-atlas-mist disabled:opacity-40"
          title={!cognosAvailable
            ? "Cognos SDK not configured"
            : "Crawl Cognos and refresh the entity inventory"}
        >
          {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh from Cognos
        </button>
      </div>
      {ingestError && (
        <p className="mt-2 rounded-md border border-atlas-danger/30 bg-atlas-danger/5 p-2 text-2xs text-atlas-danger">
          Ingest failed — {ingestError}
        </p>
      )}
    </section>
  );
}

function RunStatusBadge({ status }: { status: IngestRun["status"] }) {
  const cls =
    status === "RUNNING"
      ? "bg-atlas-primary/10 text-atlas-primary"
      : status === "SUCCESS"
      ? "bg-atlas-success/10 text-atlas-success"
      : "bg-atlas-danger/10 text-atlas-danger";
  return (
    <span className={`ml-1 rounded-full px-2 py-0.5 text-2xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

function ImpactCard({ impact }: { impact: GovernanceImpactSummary }) {
  const blockers = impact.bySeverity.BLOCKER ?? 0;
  const warns = impact.bySeverity.WARN ?? 0;
  const infos = impact.bySeverity.INFO ?? 0;
  const topCategories = Object.entries(impact.byCategory)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 4);
  const topRules = Object.entries(impact.byRule)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);
  return (
    <section
      className="mt-4 rounded-xl2 border border-atlas-slate/20 bg-white p-4 shadow-card"
      aria-label="Compliance summary"
    >
      <header className="mb-3 flex items-center gap-2 text-sm font-semibold text-atlas-ink">
        <Radar size={14} aria-hidden /> Compliance summary
      </header>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Scanned" value={impact.entitiesScanned.toString()} tone="muted" />
        <Stat label="At risk" value={impact.entitiesAtRisk.toString()} tone="info" />
        <Stat label="Blockers" value={blockers.toString()} tone={blockers > 0 ? "bad" : "ok"} />
        <Stat label="Warnings" value={warns.toString()} tone={warns > 0 ? "warn" : "muted"} />
      </div>
      {(topCategories.length > 0 || topRules.length > 0) && (
        <div className="mt-3 grid gap-3 border-t border-atlas-slate/15 pt-3 sm:grid-cols-2">
          {topCategories.length > 0 && (
            <div>
              <div className="text-2xs uppercase tracking-wide text-atlas-slate">
                Top categories
              </div>
              <ul className="mt-1 space-y-1">
                {topCategories.map(([cat, count]) => (
                  <li key={cat} className="flex items-center justify-between text-xs">
                    <span className="text-atlas-ink">{cat}</span>
                    <span className="font-mono font-medium text-atlas-ink">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {topRules.length > 0 && (
            <div>
              <div className="text-2xs uppercase tracking-wide text-atlas-slate">
                Top rules
              </div>
              <ul className="mt-1 space-y-1">
                {topRules.map(([rule, count]) => (
                  <li key={rule} className="flex items-center justify-between text-xs">
                    <span className="font-mono text-atlas-ink">{rule}</span>
                    <span className="font-mono font-medium text-atlas-ink">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {impact.topOffenders.length > 0 && (
        <div className="mt-3 border-t border-atlas-slate/15 pt-3">
          <div className="text-2xs uppercase tracking-wide text-atlas-slate flex items-center gap-1">
            <Users size={11} aria-hidden /> Top offenders
          </div>
          <ul className="mt-1 space-y-1">
            {impact.topOffenders.slice(0, 5).map((o) => (
              <li
                key={`${o.assetPlatform}:${o.assetId}`}
                className="flex items-center justify-between text-xs"
              >
                <span className="font-mono text-atlas-ink">
                  {o.assetPlatform}:{o.assetId}
                </span>
                <span className="text-2xs">
                  <span className="font-mono font-medium text-atlas-danger">{o.blockerCount}B</span>{" "}
                  <span className="font-mono font-medium text-atlas-warn">{o.warnCount}W</span>{" "}
                  <span className="text-atlas-slate">({o.totalFindings} total)</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-2 text-2xs text-atlas-slate">
        Total findings:{" "}
        <span className="font-medium text-atlas-ink">{impact.totalFindings}</span>{" "}
        ({blockers} blockers, {warns} warnings, {infos} info)
      </p>
    </section>
  );
}

function ReportView({
  report,
  search,
  setSearch,
  severityFilter,
  setSeverityFilter,
  categoryFilter,
  setCategoryFilter,
  ruleFilter,
  setRuleFilter,
  sort,
  setSort,
  groupByEntity,
  setGroupByEntity,
}: {
  report: ComplianceReport;
  search: string;
  setSearch: (s: string) => void;
  severityFilter: SeverityFilter;
  setSeverityFilter: (s: SeverityFilter) => void;
  categoryFilter: string;
  setCategoryFilter: (s: string) => void;
  ruleFilter: string;
  setRuleFilter: (s: string) => void;
  sort: SortKey;
  setSort: (s: SortKey) => void;
  groupByEntity: boolean;
  setGroupByEntity: (b: boolean) => void;
}) {
  const categories = useMemo(
    () => Array.from(new Set(report.findings.map((f) => f.category))).sort(),
    [report.findings],
  );
  const ruleIds = useMemo(
    () => Array.from(new Set(report.findings.map((f) => f.ruleId))).sort(),
    [report.findings],
  );

  const filtered = useMemo(() => {
    let out = [...report.findings];
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      out = out.filter((f) =>
        f.title.toLowerCase().includes(needle) ||
        f.details.toLowerCase().includes(needle) ||
        (f.assetId.value ?? "").toLowerCase().includes(needle) ||
        f.ruleId.toLowerCase().includes(needle),
      );
    }
    if (severityFilter !== "all") {
      out = out.filter((f) => f.severity === severityFilter);
    }
    if (categoryFilter !== "all") {
      out = out.filter((f) => f.category === categoryFilter);
    }
    if (ruleFilter !== "all") {
      out = out.filter((f) => f.ruleId === ruleFilter);
    }
    out.sort((a, b) => {
      switch (sort) {
        case "severity":
          return severityOrder(a.severity) - severityOrder(b.severity)
            || a.ruleId.localeCompare(b.ruleId);
        case "rule":
          return a.ruleId.localeCompare(b.ruleId);
        case "category":
          return a.category.localeCompare(b.category);
        case "asset":
          return (a.assetId.value ?? "").localeCompare(b.assetId.value ?? "");
        default:
          return 0;
      }
    });
    return out;
  }, [report.findings, search, severityFilter, categoryFilter, ruleFilter, sort]);

  const grouped = useMemo(() => {
    if (!groupByEntity) return null;
    const map = new Map<string, GovernanceFinding[]>();
    for (const f of filtered) {
      const key = `${f.assetId.platform}:${f.assetId.value}`;
      const list = map.get(key);
      if (list) list.push(f);
      else map.set(key, [f]);
    }
    return Array.from(map.entries());
  }, [filtered, groupByEntity]);

  const blockers = report.countsBySeverity.BLOCKER ?? 0;
  const clean = blockers === 0 && filtered.length === 0 && report.findings.length === 0;

  return (
    <section data-tour-id="governance-violations" className="mt-4 rounded-xl2 border border-atlas-slate/20 bg-white p-4 shadow-card">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-2xs">
        <div className="relative flex-1 min-w-[12rem]">
          <Search
            size={12}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-atlas-slate"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search findings (title, asset, rule)…"
            aria-label="Search findings"
            className="w-full rounded-md border border-atlas-slate/30 bg-white py-1 pl-7 pr-2 text-xs text-atlas-ink placeholder:text-atlas-slate focus:border-atlas-primary focus:outline-none focus:ring-2 focus:ring-atlas-primary/20"
          />
        </div>
        <Select
          label="Severity"
          value={severityFilter}
          onChange={(v) => setSeverityFilter(v as SeverityFilter)}
          options={[
            ["all", "Any severity"],
            ["BLOCKER", "Blocker"],
            ["WARN", "Warn"],
            ["INFO", "Info"],
          ]}
        />
        <Select
          label="Category"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            ["all", "Any category"] as [string, string],
            ...categories.map((c) => [c, c] as [string, string]),
          ]}
        />
        <Select
          label="Rule"
          value={ruleFilter}
          onChange={setRuleFilter}
          options={[
            ["all", "Any rule"] as [string, string],
            ...ruleIds.map((r) => [r, r] as [string, string]),
          ]}
        />
        <Select
          label="Sort"
          icon={<ArrowDownNarrowWide size={12} />}
          value={sort}
          onChange={(v) => setSort(v as SortKey)}
          options={[
            ["severity", "Severity"],
            ["rule", "Rule ID"],
            ["category", "Category"],
            ["asset", "Asset"],
          ]}
        />
        <label className="inline-flex items-center gap-1.5 rounded-md border border-atlas-slate/30 bg-white px-2 py-1 text-2xs text-atlas-ink">
          <input
            type="checkbox"
            checked={groupByEntity}
            onChange={(e) => setGroupByEntity(e.target.checked)}
          />
          Group by entity
        </label>
      </div>
      <div className="mb-3 text-2xs text-atlas-slate">
        Showing <span className="font-medium text-atlas-ink">{filtered.length}</span> of{" "}
        <span className="font-medium text-atlas-ink">{report.findings.length}</span> findings
      </div>

      {clean && (
        <div className="rounded-md border border-atlas-success/30 bg-atlas-success/5 p-3 text-sm text-atlas-success">
          ✓ Atlas attests this scope as governed.
        </div>
      )}

      {!clean && filtered.length === 0 && (
        <div className="rounded-md border border-dashed border-atlas-slate/30 bg-white p-4 text-center text-sm text-atlas-slate">
          No findings match your filters. Clear the search or widen the filters.
        </div>
      )}

      {filtered.length > 0 && !grouped && (
        <ul className="space-y-2 text-xs">
          {filtered.map((f, i) => <FindingRow key={i} finding={f} />)}
        </ul>
      )}

      {grouped && grouped.length > 0 && (
        <ul className="space-y-3 text-xs">
          {grouped.map(([key, findings]) => (
            <li key={key} className="rounded-md border border-atlas-slate/15 bg-atlas-fog/40 p-3">
              <div className="mb-2 flex items-center justify-between text-2xs">
                <span className="font-mono font-semibold text-atlas-ink">{key}</span>
                <span className="text-atlas-slate">{findings.length} findings</span>
              </div>
              <ul className="space-y-2">
                {findings.map((f, i) => <FindingRow key={i} finding={f} />)}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function FindingRow({ finding }: { finding: GovernanceFinding }) {
  return (
    <li className="rounded-md border border-atlas-slate/20 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-2xs">
            <span className="font-mono font-semibold text-atlas-ink">{finding.ruleId}</span>
            <span className="text-atlas-slate">{finding.category}</span>
            <span className="text-atlas-slate">·</span>
            <span className="font-mono text-atlas-slate">
              {finding.assetId.platform}:{finding.assetId.value}
            </span>
          </div>
          <p className="mt-1 text-sm font-medium text-atlas-ink">{finding.title}</p>
          {finding.details && (
            <p className="mt-0.5 text-xs text-atlas-ink/80">{finding.details}</p>
          )}
          {finding.fixHint && (
            <p className="mt-1 text-2xs text-atlas-slate">→ {finding.fixHint}</p>
          )}
        </div>
        <SeverityBadge level={finding.severity} />
      </div>
    </li>
  );
}

function severityOrder(s: Severity): number {
  return s === "BLOCKER" ? 0 : s === "WARN" ? 1 : 2;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "info" | "warn" | "bad" | "muted";
}) {
  const valueClass =
    tone === "ok"
      ? "text-atlas-success"
      : tone === "info"
      ? "text-atlas-primary"
      : tone === "warn"
      ? "text-atlas-warn"
      : tone === "bad"
      ? "text-atlas-danger"
      : "text-atlas-ink/70";
  return (
    <div className="rounded-md border border-atlas-slate/15 bg-atlas-mist/30 p-2.5">
      <div className="text-2xs uppercase tracking-wide text-atlas-slate">{label}</div>
      <div className={`mt-0.5 font-mono text-2xl font-semibold ${valueClass}`}>{value}</div>
    </div>
  );
}

function ModeButton({
  current,
  value,
  label,
  onClick,
}: {
  current: ScanMode;
  value: ScanMode;
  label: string;
  onClick: (m: ScanMode) => void;
}) {
  return (
    <button
      onClick={() => onClick(value)}
      className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
        current === value
          ? "border-atlas-primary bg-atlas-primary text-white"
          : "border-atlas-slate/30 bg-white text-atlas-ink hover:bg-atlas-mist"
      }`}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="text-2xs font-medium uppercase tracking-wide text-atlas-slate">
        {label}
      </span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  icon,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: [string, string][];
  icon?: React.ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-2xs text-atlas-slate">
      {icon}
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="rounded-md border border-atlas-slate/30 bg-white px-2 py-1 text-2xs text-atlas-ink focus:border-atlas-primary focus:outline-none focus:ring-2 focus:ring-atlas-primary/20"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
